import type { PublicPreviewDeal } from "../../services/PublicPreviewService";

/** Real finds from this ZIP are shown, labelled as examples, when the shopper's own ZIP is unknown or empty. */
export const EXAMPLE_PREVIEW_ZIP = "90012";

const APPAREL = /\b((boy)?shorts?|shirts?|tees?|t-shirts?|tops?|tanks?|jackets?|coats?|hoodies?|sweatshirts?|sweaters?|cardigans?|jeans|pants?|leggings|joggers|dress(es)?|skirts?|swim\w*|bikinis?|pajamas?|sleepwear|socks|bras?|underwear|panties|boxers?|briefs?|polos?|uniforms?|rompers?|onesies?|bodysuits?|shoes|sneakers?|sandals?|slides|boots|slippers|hats?|beanies?|vests?|robes?|tights|gloves|scarf|scarves|jerseys?|clogs?|crocs|loafers?|heels|flip[- ]flops)\b|\bsizes?\s+\w/i;

export function isApparel(name: string): boolean {
  return APPAREL.test(name);
}

const WANTED = /\b(tv|television|laptop|chromebook|tablet|ipad|monitor|headphones?|earbuds|speakers?|soundbar|camera|drone|console|playstation|ps5|xbox|nintendo|smart ?watch|vacuum|robot|mop|air fryer|blender|mixer|coffee maker|espresso|keurig|slush\w*|(?:drink|ice|ice cream|popcorn|waffle|smoothie|bread) maker|microwave|toaster|instant pot|pressure cooker|grill|smoker|heater|purifier|humidifier|drill|saw|tool set|toolbox|generator|pressure washer|mower|trimmer|blower|bike|scooter|lego|playset|dollhouse|ride-on|trampoline|kayak|patio|furniture|mattress|massager)\b/i;
const GROCERY = /\b(oz|fl\.? ?oz|lb|lbs|count|ct|pack|bottles?|cans?|snacks?|cake|cookies?|candy|chips|cereal|beef|chicken|pork|patties|frozen|refrigerated|juice|soda|water|energy drink|coffee pods?|k-?cups?|tea|creamer|yogurt|cheese|bread|sauce|seasoning|vitamins?|shampoo|conditioner|deodorant|toothpaste|lotion|detergent|paper towels?|toilet paper|diapers?|wipes)\b/i;

/** 0 = most wanted (electronics, appliances, vacuums, toys, tools), 1 = other, 2 = groceries and drinks, 3 = clothing. */
export function dealTier(name: string): number {
  if (isApparel(name)) return 3;
  if (WANTED.test(name)) return 0;
  if (GROCERY.test(name)) return 2;
  return 1;
}

/** Most wanted finds first; clothing is most of the clearance feed and the least exciting. Stable within a tier. */
export function prioritizeNonApparel(deals: PublicPreviewDeal[]): PublicPreviewDeal[] {
  return deals
    .map((deal, index) => ({ deal, index, tier: dealTier(deal.name) }))
    .sort((a, b) => a.tier - b.tier || a.index - b.index)
    .map((entry) => entry.deal);
}

/**
 * Shuffles the top deals so free and signed-out shoppers don't see the same lead
 * deal every visit. Only the first `pool` items rotate (the feed arrives biggest
 * discount first), so the picks stay among the strongest deals.
 */
export function rotateDeals<T>(items: T[], pool = items.length, random: () => number = Math.random): T[] {
  const head = items.slice(0, pool);
  for (let i = head.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [head[i], head[j]] = [head[j], head[i]];
  }
  return [...head, ...items.slice(pool)];
}

/** Drops repeats of the same item (same name and photo), which the feeds list once per size or variant. */
export function dedupeByLook<T extends { name: string; image?: string }>(items: T[]): T[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = `${item.name.trim().toLowerCase()}|${item.image || ""}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

const APPAREL_CATEGORY = /cloth|apparel|fashion|shoe|accessor|baby/i;

/** Biggest categories first, clothing-like ones pushed to the end. */
export function orderCategoryRows<T extends { id: string; count: number }>(categories: T[], limit = 6): T[] {
  const ranked = [...categories].filter((item) => item.id && item.count >= 4).sort((a, b) => b.count - a.count);
  return [...ranked.filter((item) => !APPAREL_CATEGORY.test(item.id)), ...ranked.filter((item) => APPAREL_CATEGORY.test(item.id))].slice(0, limit);
}
